import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import ActionButton from './ActionButton';
import { dealTokens } from './designTokens';

const Chevron = ({ open }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
    style={{ transition: 'transform .15s', transform: open ? 'rotate(180deg)' : 'none', flexShrink: 0 }}
  >
    <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Check = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M2.5 7.5l3 3 6-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Every style is inline on purpose: the menu is dropped into hosts that style bare
// `button` elements (react-big-calendar's `.rbc-toolbar button`), and inline wins.
const itemStyle = ({ disabled, active, hovered }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  gap: dealTokens.space[2],
  width: '100%',
  height: 'auto',
  minHeight: dealTokens.control.md,
  margin: 0,
  padding: '9px 12px',
  border: 'none',
  borderRadius: dealTokens.radius.sm,
  background: hovered && !disabled ? dealTokens.color.canvas : 'transparent',
  color: dealTokens.color.text,
  fontFamily: 'inherit',
  fontSize: 13,
  fontWeight: active ? dealTokens.font.weightSemibold : dealTokens.font.weightRegular,
  lineHeight: '18px',
  textAlign: 'left',
  whiteSpace: 'normal',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
  boxShadow: 'none',
});

const MenuItem = ({ item, onPick }) => {
  const [hovered, setHovered] = useState(false);
  const active = Boolean(item.selected || item.checked);
  return (
    <button
      type="button"
      role={item.checked !== undefined ? 'menuitemcheckbox' : item.selected !== undefined ? 'menuitemradio' : 'menuitem'}
      aria-checked={item.checked !== undefined || item.selected !== undefined ? active : undefined}
      disabled={item.disabled}
      onClick={() => onPick(item)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={itemStyle({ disabled: item.disabled, active: Boolean(item.selected), hovered })}
    >
      <span
        style={{
          width: 16,
          flexShrink: 0,
          display: 'inline-flex',
          paddingTop: 1,
          color: active ? dealTokens.color.primary : dealTokens.color.textMuted,
        }}
      >
        {active ? <Check /> : item.icon && React.cloneElement(item.icon, { style: { fontSize: 16, ...(item.icon.props?.style ?? {}) } })}
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <span>{item.label}</span>
        {item.hint && <span style={{ fontSize: 11.5, color: dealTokens.color.textMuted, marginTop: 2 }}>{item.hint}</span>}
      </span>
    </button>
  );
};

/**
 * Toolbar dropdown with grouped choices — e.g. the calendar's view switcher plus its display
 * options — or a list of actions behind one primary button (`variant="primary"`, e.g. a
 * calendar's "+ New"). The trigger is a SystemButton showing `label` (or only `icon`, e.g. a
 * settings gear — then `ariaLabel` names it and doubles as its tooltip); the panel lists `sections`
 * separated by dividers, each with an optional `title` and its `items`:
 *   { key, label, selected }  one-of-many choice (a view, a format) — bold with a check
 *   { key, label, checked }   on/off option — a check when on
 *   { key, label, icon }      plain action (e.g. "create a reservation") — its icon in the same column
 * An item can be `disabled` and carry a `hint` explaining why; a whole section can too
 * (`disabled` + `hint` on the section, shown once under its title). Picking an item calls its
 * `onSelect` and closes the menu. `align` sets which edge of the trigger the panel follows.
 * The panel is portaled to <body> with a fixed position, so hosts with their own stacking
 * contexts or clipping (react-big-calendar's rows) cannot cover or cut it.
 */
const SystemMenuButton = ({ label, icon, variant = 'ghost', sections = [], ariaLabel, align = 'right', minWidth = 240 }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const rootRef = useRef(null);
  const panelRef = useRef(null);

  useLayoutEffect(() => {
    if (!open || !rootRef.current) return undefined;
    const update = () => {
      const rect = rootRef.current.getBoundingClientRect();
      const next = align === 'left' ? { top: rect.bottom + 4, left: rect.left } : { top: rect.bottom + 4, right: window.innerWidth - rect.right };
      // Same object when nothing moved, so a scroll elsewhere doesn't re-render the panel.
      setPosition(prev => (prev && prev.top === next.top && prev.left === next.left && prev.right === next.right ? prev : next));
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, align]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = e => {
      const inside = [rootRef.current, panelRef.current].some(node => node && node.contains(e.target));
      if (!inside) setOpen(false);
    };
    const onKey = e => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const visibleSections = sections.filter(section => section.items?.length);
  const pick = item => {
    if (item.disabled) return;
    setOpen(false);
    item.onSelect?.();
  };

  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'inline-flex' }}>
      {label ? (
        <ActionButton variant={variant} icon={icon} onClick={() => setOpen(o => !o)} aria-haspopup="menu" aria-expanded={open} aria-label={ariaLabel}>
          {label}
          <Chevron open={open} />
        </ActionButton>
      ) : (
        <ActionButton
          variant="ghost"
          icon={icon}
          onClick={() => setOpen(o => !o)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={ariaLabel}
          tooltip={open ? undefined : ariaLabel}
          style={{ width: dealTokens.control.md, minWidth: dealTokens.control.md, padding: 0 }}
        />
      )}
      {open &&
        position &&
        createPortal(
          <div
            ref={panelRef}
            role="menu"
            style={{
              position: 'fixed',
              ...position,
              zIndex: 1300,
              minWidth,
              padding: dealTokens.space[1],
              background: dealTokens.color.surface,
              border: `1px solid ${dealTokens.color.border}`,
              borderRadius: dealTokens.radius.lg,
              boxShadow: dealTokens.shadow.floating,
            }}
          >
            {visibleSections.map((section, index) => (
              <div
                key={section.key || index}
                style={index > 0 ? { borderTop: `1px solid ${dealTokens.color.border}`, marginTop: 4, paddingTop: 4 } : undefined}
              >
                {section.title && (
                  <div
                    style={{
                      padding: '6px 12px 2px 36px',
                      fontSize: 11,
                      fontWeight: dealTokens.font.weightSemibold,
                      color: dealTokens.color.textMuted,
                    }}
                  >
                    {section.title}
                    {section.disabled && section.hint && (
                      <div style={{ fontWeight: dealTokens.font.weightRegular, fontSize: 11.5, marginTop: 2 }}>{section.hint}</div>
                    )}
                  </div>
                )}
                {section.items.map(item => (
                  <MenuItem key={item.key} item={section.disabled ? { ...item, disabled: true } : item} onPick={pick} />
                ))}
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

export default SystemMenuButton;
